import { downloadText } from '../utils/download';
import { FloorPlan } from './FloorPlanService';

export interface FloorPlanMetadata {
  venueId?: string;
  venueName?: string;
  eventId?: string;
  eventName?: string;
  floorPlanName?: string;
  zones?: Array<{
    id: string;
    name: string;
    capacity?: number;
    type?: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  }>;
  tables?: Array<{
    id: string;
    name: string;
    x: number;
    y: number;
    width?: number;
    height?: number;
    capacity?: number;
    section?: string;
    shape?: 'circle' | 'square' | 'rectangle' | 'crescent' | 'rectangular';
  }>;
  dimensions?: {
    width: number;
    height: number;
    unit?: 'ft' | 'm' | 'px';
  };
  scale?: number;
  createdDate?: string;
  lastModified?: string;
  appVersion?: string;
}

export function generateSvgWithMetadata(
  canvasElement: HTMLElement,
  metadata: FloorPlanMetadata,
  width: number = 1000,
  height: number = 700
): string {
  // Create SVG element
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

  // Add background
  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bg.setAttribute('width', String(width));
  bg.setAttribute('height', String(height));
  bg.setAttribute('fill', '#fafafa');
  bg.setAttribute('stroke', '#e0e0e0');
  bg.setAttribute('stroke-width', '2');
  svg.appendChild(bg);

  // Add metadata as SVG comment
  const metadataComment = document.createComment(
    `Anojeje Floor Plan Metadata|${JSON.stringify(metadata)}`
  );
  svg.appendChild(metadataComment);

  // Clone canvas content into SVG
  const canvasClone = canvasElement.cloneNode(true) as HTMLElement;

  // Convert SVG elements if any exist
  const svgElements = canvasClone.querySelectorAll('svg');
  svgElements.forEach(el => {
    const children = Array.from(el.childNodes);
    children.forEach(child => {
      const clonedChild = child.cloneNode(true);
      svg.appendChild(clonedChild);
    });
  });

  // Convert canvas drawing elements
  const divs = canvasClone.querySelectorAll('div[data-room-id], div[data-table-id]');
  divs.forEach(div => {
    const rect = (div as HTMLElement).getBoundingClientRect();
    const containerRect = canvasElement.getBoundingClientRect();

    const x = rect.left - containerRect.left;
    const y = rect.top - containerRect.top;
    const w = rect.width;
    const h = rect.height;

    const el = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    el.setAttribute('x', String(x));
    el.setAttribute('y', String(y));
    el.setAttribute('width', String(w));
    el.setAttribute('height', String(h));
    el.setAttribute('fill', getComputedStyle(div as HTMLElement).backgroundColor || '#f0f0f0');
    el.setAttribute('stroke', getComputedStyle(div as HTMLElement).borderColor || '#999');
    el.setAttribute('stroke-width', '1');

    // Preserve data attributes
    const roomId = (div as HTMLElement).getAttribute('data-room-id');
    const tableId = (div as HTMLElement).getAttribute('data-table-id');
    if (roomId) el.setAttribute('data-room-id', roomId);
    if (tableId) el.setAttribute('data-table-id', tableId);

    svg.appendChild(el);
  });

  return new XMLSerializer().serializeToString(svg);
}

export function downloadSvgFloorPlan(
  svgContent: string,
  fileName: string = 'floor-plan.svg'
): void {
  downloadText(fileName, svgContent, 'image/svg+xml');
}

// ── Fabric.js-native export (migration gap 5/11) ───────────────────────────
// Unlike generateSvgWithMetadata() above (which scrapes DOM divs the old
// hand-rolled canvas rendered), the Fabric editor draws to a <canvas>, so
// there is no DOM to scrape. fabric.Canvas#toSVG() already serialises every
// shape/text object as real vector markup — we just need to inject the same
// `Anojeje Floor Plan Metadata|{...}` comment so parseSvgMetadata() above
// (used by the Fabric editor's SVG import) round-trips cleanly.
export interface FabricExportRoom {
    id: string;
    name: string;
    type: string;
    capacity?: number;
    x: number;
    y: number;
    width: number;
    height: number;
    tableShape?: string;
    /** section letter (A to D) the table sits in, derived from its position */
    section?: string;
}

type SvgTableShape = NonNullable<NonNullable<FloorPlanMetadata['tables']>[number]['shape']>;

/** Table shape as written into the SVG metadata. Existing shapes keep their original names. */
export function svgShapeForTable(tableShape?: string): SvgTableShape {
    switch (tableShape) {
        case 'round': return 'circle';
        case 'rect': return 'square';
        case 'crescent': return 'crescent';
        case 'rectangular': return 'rectangular';
        default: return 'rectangle';
    }
}

/** Inverse of svgShapeForTable, for import. 'rectangle' stays the legacy oval. */
export function tableShapeFromSvg(shape?: string): 'round' | 'rect' | 'oval' | 'crescent' | 'rectangular' {
    switch (shape) {
        case 'circle': return 'round';
        case 'square': return 'rect';
        case 'crescent': return 'crescent';
        case 'rectangular': return 'rectangular';
        default: return 'oval';
    }
}

export interface FabricSvgExportOptions {
    venueId?: string;
    venueName?: string;
    floorPlanName?: string;
    eventId?: string;
    eventName?: string;
    rooms: FabricExportRoom[];
    dimensions?: { width: number; height: number };
    scale?: number;
}

/**
 * canvas.toSVG(...) — the Fabric canvas instance, typed loosely here to avoid
 * pulling the `fabric` package into this service module.
 */
export function exportFabricFloorPlanSvg(
    canvas: { toSVG: (options?: Record<string, unknown>) => string },
    options: FabricSvgExportOptions,
): string {
    const metadata: FloorPlanMetadata = {
        venueId: options.venueId || undefined,
        venueName: options.venueName,
        eventId: options.eventId,
        eventName: options.eventName,
        floorPlanName: options.floorPlanName,
        zones: options.rooms
            .filter(r => r.type !== 'table')
            .map(r => ({ id: r.id, name: r.name, capacity: r.capacity, type: r.type, x: r.x, y: r.y, width: r.width, height: r.height })),
        tables: options.rooms
            .filter(r => r.type === 'table')
            .map(r => ({
                id: r.id, name: r.name, x: r.x, y: r.y, width: r.width, height: r.height, capacity: r.capacity,
                section: r.section,
                shape: svgShapeForTable(r.tableShape),
            })),
        dimensions: options.dimensions ? { ...options.dimensions, unit: 'px' } : undefined,
        scale: options.scale,
        createdDate: new Date().toISOString(),
        appVersion: '1.0.0',
    };

    const svgString = canvas.toSVG();
    const metadataComment = `<!-- Anojeje Floor Plan Metadata|${JSON.stringify(metadata)} -->`;
    // fabric emits an XML prolog + <svg ...> opening tag on their own lines —
    // insert right after the opening tag so the comment stays valid, well-formed SVG.
    const insertAt = svgString.indexOf('>') + 1;
    return `${svgString.slice(0, insertAt)}\n${metadataComment}\n${svgString.slice(insertAt)}`;
}

export function parseSvgMetadata(svgContent: string): FloorPlanMetadata | null {
  try {
    // Extract comment with metadata
    const commentMatch = svgContent.match(/<!--\s*Anojeje Floor Plan Metadata\|(.+?)\s*-->/);
    if (!commentMatch || !commentMatch[1]) {
      return null;
    }

    const metadata = JSON.parse(commentMatch[1]);
    return metadata as FloorPlanMetadata;
  } catch (error) {
    console.error('Failed to parse SVG metadata:', error);
    return null;
  }
}

export function createMetadataFromFloorPlan(
  floorPlan: FloorPlan,
  eventName?: string,
  eventId?: string,
  venueName?: string,
  rooms?: Array<any>,
  tables?: Array<any>
): FloorPlanMetadata {
  return {
    venueId: floorPlan.venue_id,
    venueName,
    eventId,
    eventName,
    floorPlanName: floorPlan.name,
    zones: rooms?.map(room => ({
      id: room.id,
      name: room.name,
      capacity: room.capacity,
      type: room.room_type,
      x: room.x,
      y: room.y,
      width: room.width,
      height: room.height,
    })),
    tables: tables?.map(table => ({
      id: table.id,
      name: table.table_number || table.name,
      x: table.x,
      y: table.y,
      capacity: table.capacity,
      section: table.section,
      shape: table.shape,
    })),
    dimensions: {
      width: floorPlan.width || 1000,
      height: floorPlan.height || 700,
      unit: 'px',
    },
    scale: floorPlan.scale,
    createdDate: floorPlan.created_at,
    lastModified: floorPlan.updated_at,
    appVersion: '1.0.0',
  };
}
